import React from 'react';

import { AbsoluteFill, Sequence, Series, useVideoConfig } from 'remotion';

import { AudioSequence } from '@/Remotion/VideoTemplateComponents/AudioSequence';
import { TextSequence } from '@/Remotion/VideoTemplateComponents/TextSequence';
import { VideoSequence } from '@/Remotion/VideoTemplateComponents/VideoSequence';
import {
  DEFAULT_TEXT_DURATION,
  DEFAULT_TEXT_START_FROM_END_TIME,
  DEFAULT_TEXT_START_TIME,
  DEFAULT_TRANSITION_DURATION,
  IMAGE,
  INTRO_OUTRO_DEFAULT_DURATION,
  MAX_VIDEO_PERSONAL_ASSET_DURATION,
  STOCK_CLIP,
  TEXT_OVERLAY,
  VIDEO
} from '@/constants';
import { VideoType } from '@/enums/RemotionEnums';
import { ImageMovement, TributeVideoAsset, VideoThemeText } from '@/types';

import { ImagePersonalAsset } from './VideoTemplateComponents/ImagePersonalAsset';
import { VideoPersonalAsset } from './VideoTemplateComponents/VideoPersonalAsset';

type AudioTrack = { src: string; duration: number; label?: string };

type MainVideoTemplateProps = {
  assets: TributeVideoAsset[];
  introSrc: string;
  mainSrc: string[];
  outroSrc: string;
  introFrames: number;
  mainFrames: number[];
  outroFrames: number;
  hasIntro: boolean;
  hasOutro: boolean;
  audioTracks: AudioTrack[];
  textEnabled: boolean;

  nameOfTheDeceasedFromStart: VideoThemeText | undefined;
  nameOfTheDeceasedFromEnd: VideoThemeText | undefined;
  title: VideoThemeText | undefined;
  birthDeath: VideoThemeText | undefined;

  titleText?: string | undefined;
  deceasedNameText: string | undefined;
  birthDeathText: string | undefined;

  hasMusic: boolean;
  imageBordersEnabled: boolean;
  imageBorder: string | undefined;
  movementBundleId: number;

  imageMovements: ImageMovement[];
};

export const MainVideoTemplate: React.FC<MainVideoTemplateProps> = ({
  assets,
  introSrc,
  mainSrc,
  outroSrc,
  introFrames,
  mainFrames,
  outroFrames,
  hasIntro,
  hasOutro,
  audioTracks,
  textEnabled,
  nameOfTheDeceasedFromStart,
  nameOfTheDeceasedFromEnd,
  title,
  birthDeath,
  deceasedNameText,
  birthDeathText,
  titleText,
  hasMusic,
  imageBordersEnabled,
  imageBorder,
  movementBundleId,
  imageMovements
}) => {
  const { fps, durationInFrames } = useVideoConfig();
  const assetsDisplayFirstFrame = hasIntro
    ? introFrames
    : textEnabled
      ? (INTRO_OUTRO_DEFAULT_DURATION + DEFAULT_TEXT_DURATION) * fps
      : INTRO_OUTRO_DEFAULT_DURATION * fps;
  const assetsDisplayLastFrame =
    durationInFrames - (hasOutro ? outroFrames : INTRO_OUTRO_DEFAULT_DURATION * fps);

  const displayingAssetsDuration = assetsDisplayLastFrame - assetsDisplayFirstFrame;

  const nonImageAssets = assets.filter((a) => [STOCK_CLIP, VIDEO].includes(a.asset.assetType));

  const nonImageAssetsDuration =
    nonImageAssets.reduce((sum, a) => {
      return Math.min(MAX_VIDEO_PERSONAL_ASSET_DURATION, sum + (a.asset.duration ?? 0));
    }, 0) * fps;

  const imageAndTextOverlayAssets = assets.filter((a) =>
    [IMAGE, TEXT_OVERLAY].includes(a.asset.assetType)
  );
  const singleImageDuration =
    nonImageAssets.length === 0
      ? displayingAssetsDuration / imageAndTextOverlayAssets.length
      : (displayingAssetsDuration - nonImageAssetsDuration) / imageAndTextOverlayAssets.length;

  const assetToDisplay = assets.filter((a) =>
    [IMAGE, TEXT_OVERLAY, VIDEO, STOCK_CLIP].includes(a.asset.assetType)
  );

  const backgroundDuration =
    hasIntro && hasOutro
      ? durationInFrames - introFrames - outroFrames + DEFAULT_TRANSITION_DURATION * fps * 2
      : hasIntro
        ? durationInFrames - introFrames + DEFAULT_TRANSITION_DURATION * fps
        : hasOutro
          ? durationInFrames - outroFrames + DEFAULT_TRANSITION_DURATION * fps
          : durationInFrames;

  const totalMainArrayFrames = mainFrames.reduce((s, f) => s + f, 0);
  const repeatingBackgroundVideosNumber = Math.ceil(backgroundDuration / totalMainArrayFrames);
  let backgroundAssetStart = hasIntro ? introFrames - DEFAULT_TRANSITION_DURATION * fps : 0;

  const repeatingBackgroundSrcArray = Array.from(
    { length: repeatingBackgroundVideosNumber },
    () => mainSrc
  ).flat();

  const repFrames = Array.from(
    { length: repeatingBackgroundVideosNumber },
    () => mainFrames
  ).flat();

  const repFramesSum = repFrames.reduce((s, f) => s + f, 0);
  repFrames[repeatingBackgroundVideosNumber - 1] -= repFramesSum - backgroundDuration;

  const nameOfTheDeceasedFromStartFirstFrame =
    nameOfTheDeceasedFromStart && hasIntro
      ? nameOfTheDeceasedFromStart.startTime * fps
      : DEFAULT_TEXT_START_TIME * fps;

  const nameOfTheDeceasedFromEndFirstFrame =
    nameOfTheDeceasedFromEnd && hasOutro
      ? durationInFrames - nameOfTheDeceasedFromEnd.startTime * fps
      : durationInFrames - DEFAULT_TEXT_START_FROM_END_TIME * fps;

  const titleFirstFrame = title && hasIntro ? title.startTime * fps : DEFAULT_TEXT_START_TIME * fps;

  const birthDeathFirstFrame =
    birthDeath && hasIntro ? birthDeath.startTime * fps : DEFAULT_TEXT_START_TIME * fps;

  const nameOfTheDeceasedFromStartDuration =
    nameOfTheDeceasedFromStart && hasIntro
      ? (nameOfTheDeceasedFromStart.endTime - nameOfTheDeceasedFromStart.startTime) * fps
      : DEFAULT_TEXT_DURATION * fps;

  const nameOfTheDeceasedFromEndDuration =
    nameOfTheDeceasedFromEnd && hasOutro
      ? (nameOfTheDeceasedFromEnd.startTime - nameOfTheDeceasedFromEnd.endTime) * fps
      : DEFAULT_TEXT_START_FROM_END_TIME * fps;

  const titleDuration =
    title && hasIntro ? (title.endTime - title.startTime) * fps : DEFAULT_TEXT_DURATION * fps;

  const birthDeathDuration =
    birthDeath && hasIntro
      ? (birthDeath.endTime - birthDeath.startTime) * fps
      : DEFAULT_TEXT_DURATION * fps;

  return (
    <AbsoluteFill>
      {hasMusic && <AudioSequence audioTracks={audioTracks} />}

      {hasIntro && <VideoSequence from={0} durationInFrames={introFrames} videoSrc={introSrc} />}

      {repeatingBackgroundSrcArray.map((src, i) => {
        backgroundAssetStart =
          i === 0 ? backgroundAssetStart : backgroundAssetStart + repFrames[i - 1];
        return (
          <VideoSequence
            key={i}
            from={backgroundAssetStart}
            durationInFrames={repFrames[i]}
            videoSrc={src}
            videoType={VideoType.BACKGROUND}
            firstBackgroundVideo={i === 0}
            lastBackgroundVideo={i === repeatingBackgroundSrcArray.length - 1}
          />
        );
      })}

      {hasOutro && (
        <VideoSequence
          from={
            introFrames +
            backgroundDuration -
            (hasIntro ? 2 : 1) * DEFAULT_TRANSITION_DURATION * fps
          }
          durationInFrames={outroFrames}
          videoSrc={outroSrc}
        />
      )}

      <Sequence from={assetsDisplayFirstFrame} durationInFrames={displayingAssetsDuration}>
        <Series>
          {assetToDisplay.map((a, i) => (
            <Series.Sequence
              key={i}
              durationInFrames={
                [IMAGE, TEXT_OVERLAY].includes(a.asset.assetType)
                  ? singleImageDuration
                  : a.asset.duration && a.asset.duration < MAX_VIDEO_PERSONAL_ASSET_DURATION
                    ? a.asset.duration * fps
                    : MAX_VIDEO_PERSONAL_ASSET_DURATION * fps
              }>
              {[IMAGE, TEXT_OVERLAY].includes(a.asset.assetType) ? (
                <ImagePersonalAsset
                  src={a.asset.assetUrl}
                  imageBordersEnabled={imageBordersEnabled}
                  imageBorder={imageBorder}
                  movementBundleId={movementBundleId}
                  currentImageIndex={i}
                  imageMovements={imageMovements}
                  isAssetTextOverlay={a.asset.assetType === TEXT_OVERLAY}
                />
              ) : (
                <VideoPersonalAsset src={a.asset.assetUrl} />
              )}
            </Series.Sequence>
          ))}
        </Series>
      </Sequence>

      {textEnabled && nameOfTheDeceasedFromStart && (
        <TextSequence
          from={nameOfTheDeceasedFromStartFirstFrame}
          durationInFrames={nameOfTheDeceasedFromStartDuration}
          textStyle={nameOfTheDeceasedFromStart}
          textValue={deceasedNameText}
        />
      )}

      {textEnabled && nameOfTheDeceasedFromEnd && (
        <TextSequence
          from={nameOfTheDeceasedFromEndFirstFrame}
          durationInFrames={nameOfTheDeceasedFromEndDuration}
          textStyle={nameOfTheDeceasedFromEnd}
          textValue={deceasedNameText}
        />
      )}

      {textEnabled && title && (
        <TextSequence
          from={titleFirstFrame}
          durationInFrames={titleDuration}
          textStyle={title}
          textValue={titleText}
        />
      )}

      {textEnabled && birthDeath && (
        <TextSequence
          from={birthDeathFirstFrame}
          durationInFrames={birthDeathDuration}
          textStyle={birthDeath}
          textValue={birthDeathText}
        />
      )}
    </AbsoluteFill>
  );
};
